/**
 * 3D Designer sandbox — remembers the plan being edited across a page refresh
 * and across dev-server hot reloads, so a live code change doesn't throw the
 * layout (or the locked wall) away.
 *
 * Stored in localStorage under one versioned key. Anything unreadable or not
 * shaped like a designer document is ignored, and the sandbox starts from its
 * default preset instead. A sandbox saved before floor plans existed (v1: one
 * room) is carried over as a one-room plan.
 */

import { SurfaceFace, isCeilingFace } from '../ui/room3d/types';
import { FloorPlan, planFromSingleRoom } from '../../calculations/plan';
import { isPlan, isSurfaces } from './designerDocument';

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

const KEY = 'designer.sandbox.v2';
const LEGACY_KEY = 'designer.sandbox.v1';
const VERSION = 2;

export interface DesignerUiState {
  lockedFace: SurfaceFace | null;
  selectedSectionId: string | null;
}

export interface SandboxState {
  presetId: string;
  plan: FloorPlan;
  /** The room the 3D view shows. */
  focusRoomId: string | null;
  plannerOpen: boolean;
  ui: DesignerUiState;
}

const FACES = ['front', 'back', 'left', 'right'];

/** The lock and selection out of a stored payload (shared with Advanced mode's store). */
export function uiOf(data: { ui?: { lockedFace?: unknown; selectedSectionId?: unknown } }): DesignerUiState {
  const face = data.ui?.lockedFace;
  const lockedFace = typeof face === 'string' && (FACES.includes(face) || isCeilingFace(face)) ? (face as DesignerUiState['lockedFace']) : null;
  const selectedSectionId = typeof data.ui?.selectedSectionId === 'string' ? data.ui.selectedSectionId : null;
  return { lockedFace, selectedSectionId };
}

/** Parse a stored payload. Exported for tests; returns null for anything unusable. */
export function parseSandboxState(raw: string | null): SandboxState | null {
  if (!raw) return null;
  try {
    const data = JSON.parse(raw);
    if (!data || typeof data.presetId !== 'string') return null;

    // v1: one room, as { dims, room }.
    if (data.v === 1) {
      const doc = data.doc;
      if (!doc || !doc.dims || !isNum(doc.dims.length) || doc.dims.length <= 0
        || !isNum(doc.dims.width) || doc.dims.width <= 0
        || !isNum(doc.dims.height) || doc.dims.height <= 0
        || !isSurfaces(doc.room)) return null;
      const plan = planFromSingleRoom(doc.dims, doc.room);
      return { presetId: data.presetId, plan, focusRoomId: plan.rooms[0].id, plannerOpen: false, ui: uiOf(data) };
    }

    if (data.v !== VERSION || !isPlan(data.plan)) return null;
    const plan: FloorPlan = data.plan;
    const focusRoomId = plan.rooms.some((r) => r.id === data.focusRoomId) ? data.focusRoomId : plan.rooms[0]?.id ?? null;
    return { presetId: data.presetId, plan, focusRoomId, plannerOpen: data.plannerOpen === true, ui: uiOf(data) };
  } catch {
    return null;
  }
}

export function loadSandboxState(): SandboxState | null {
  try {
    return parseSandboxState(window.localStorage.getItem(KEY))
      ?? parseSandboxState(window.localStorage.getItem(LEGACY_KEY));
  } catch {
    return null; // storage blocked
  }
}

export function saveSandboxState(state: SandboxState): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ v: VERSION, ...state }));
  } catch {
    // storage blocked or full — the sandbox still works, it just won't remember
  }
}

export function clearSandboxState(): void {
  try {
    window.localStorage.removeItem(KEY);
    window.localStorage.removeItem(LEGACY_KEY);
  } catch {
    // ignore
  }
}
